import React, { useState } from 'react';

function ProductPage() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  // Card component with hover content
  const CategoryCard = ({ 
    id, 
    title, 
    description 
  }: { 
    id: number; 
    title: string; 
    description: string; 
  }) => (
    <div 
      className="relative w-[370px] h-[300px] bg-gradient-to-br from-[#5FAAA4] to-[#4A8F8A] rounded-2xl shadow-lg cursor-pointer 
                transition-all duration-500 ease-in-out hover:scale-105 hover:-translate-y-2 hover:shadow-2xl overflow-hidden group"
      onMouseEnter={() => setHoveredCard(id)}
      onMouseLeave={() => setHoveredCard(null)}
    >
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-20 h-20 bg-white rounded-full -translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-1000"></div>
        <div className="absolute bottom-0 right-0 w-16 h-16 bg-amber-300 rounded-full translate-x-8 translate-y-8 group-hover:scale-150 transition-transform duration-1000 delay-200"></div>
      </div>
      
      {/* Base Card Background with Animation */}
      <div className={`absolute inset-0 bg-gradient-to-br from-[#5FAAA4] to-[#4A8F8A] transition-all duration-700 ${
        hoveredCard === id ? 'opacity-90 scale-110' : 'opacity-100 scale-100'
      }`}></div>
      
      {/* Hover Overlay Content */}
      <div className={`absolute inset-0 flex flex-col justify-center items-center p-8 text-white 
        transition-all duration-700 transform ${
        hoveredCard === id 
          ? 'opacity-100 translate-y-0 bg-gradient-to-br from-black/80 to-gray-900/80' 
          : 'opacity-0 translate-y-8'
      }`}>
        <div className="transform transition-all duration-500 delay-200">
          <h3 className="text-2xl font-bold mb-4 text-center text-amber-300 animate-pulse-slow">{title}</h3>
        </div>
        <div className="transform transition-all duration-500 delay-300">
          <p className="text-sm leading-relaxed text-center text-gray-200 max-w-xs">{description}</p>
        </div>
        
        {/* Animated View Button */}
        <div className={`mt-6 transform transition-all duration-500 delay-400 ${
          hoveredCard === id ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}>
          {/* Empty button container - you can add button here if needed */}
        </div>
      </div>

      {/* Default State Icon with Animation */}
      <div className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
        hoveredCard === id ? 'opacity-0 scale-75 rotate-12' : 'opacity-100 scale-100 rotate-0'
      }`}>
        <div className="text-white text-center transform transition-all duration-300 group-hover:scale-110">
          <div className="text-5xl mb-4 transform transition-transform duration-700 group-hover:rotate-12 group-hover:scale-125">
            {id === 1 ? '🌾' : id === 2 ? '🏭' : id === 3 ? '📱' : 
             id === 4 ? '👕' : id === 5 ? '🚗' : id === 6 ? '🏥' :
             id === 7 ? '💡' : id === 8 ? '🌿' : '☀'}
          </div>
          <h3 className="text-xl font-semibold bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full 
                        transform transition-all duration-300 group-hover:bg-white/30 group-hover:scale-105">
            {title.split(' ')[0]}
          </h3>
        </div>
      </div>

      {/* Animated Border */}
      <div className={`absolute inset-0 rounded-2xl border-2 transition-all duration-500 ${
        hoveredCard === id 
          ? 'border-amber-400 scale-105' 
          : 'border-white/20 scale-100'
      }`}></div>
    </div>
  );

  // Section title component with animation
  const SectionTitle = ({ title, delay = 0 }: { title: string; delay?: number }) => (
    <div 
      className="text-center mb-16 transform transition-all duration-700"
      style={{ animationDelay: `${delay}ms` }}
    >
      <h2 className="text-4xl font-bold text-gray-800 mb-4 animate-fade-in-up">{title}</h2>
      <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full 
                     transform transition-all duration-1000 hover:scale-125 hover:shadow-lg"></div>
    </div>
  );

  // Sample data for cards
  const featuredCategories = [
    {
      id: 1,
      title: "Agricultural Products",
      description: "High-quality agricultural commodities including perishable items such as fresh grains, fruits, and vegetables, as well as non-perishable products like pulses, dry fruits, spices, and processed foods — all sourced from trusted farmers worldwide."
    },
    {
      id: 2,
      title: "Industrial Materials",
      description: "Comprehensive range of industrial raw materials and components for manufacturing industries with quality assurance."
    },
    {
      id: 3,
      title: "Consumer Goods",
      description: "we are resourcing from china at any MOQ China to GCC/UAE. Wide variety of consumer products including electronics and home appliances with competitive pricing.."
    }
  ];

  const newArrivals = [
    {
      id: 7,
      title: "Smart Technology",
      description: "Latest smart devices and IoT solutions for modern living with cutting-edge technology integration."
    },
    {
      id: 8,
      title: "Organic Products",
      description: "Certified organic food items and natural products with sustainable sourcing practices."
    },
    {
      id: 9,
      title: "Running business sailing",
      description: "Running Business Sailing while running a business demands location independence and high-level self-discipline; "
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50/30">
      {/* Header with Enhanced Animation */}
      <header className="py-16 bg-gradient-to-r from-teal-50/80 to-amber-50/80 backdrop-blur-sm relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-amber-200/30 rounded-full animate-float"></div>
          <div className="absolute top-20 right-20 w-16 h-16 bg-teal-200/40 rounded-full animate-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-10 left-1/4 w-12 h-12 bg-amber-300/20 rounded-full animate-float" style={{animationDelay: '4s'}}></div>
        </div>
        
        <div className="relative z-10 text-center">
          <div className="inline-block mb-6 transform transition-all duration-1000 hover:scale-110">
            <div className="w-24 h-1 bg-amber-400 rounded-full mb-2 animate-pulse"></div>
            <div className="w-20 h-1 bg-amber-300 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6 animate-fade-in-up">
            OUR <span className="text-amber-500 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent animate-pulse-slow">PRODUCTS</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto animate-fade-in-up" style={{animationDelay: '0.3s'}}>
            Discover our wide range of high-quality products tailored to meet your business needs
          </p>
        </div>
      </header>

      <main className="container mx-auto px-6 py-16">
        {/* Featured Categories */}
        <section className="mb-24 animate-fade-in-up">
          <SectionTitle title="Featured Categories" delay={100} />
          <div className="flex justify-center gap-10 flex-wrap">
            {featuredCategories.map((category, index) => (
              <div 
                key={category.id} 
                className="group transform transition-all duration-700 hover:scale-105"
                style={{ animationDelay: `${200 + index * 100}ms` }}
              >
                <CategoryCard 
                  id={category.id}
                  title={category.title}
                  description={category.description}
                />
              </div>
            ))}
          </div>
        </section>

        {/* New Arrivals */}
        <section className="mb-24 animate-fade-in-up" style={{animationDelay: '400ms'}}>
          <SectionTitle title="New Arrivals" delay={500} />
          <div className="flex justify-center gap-10 flex-wrap">
            {newArrivals.map((category, index) => (
              <div 
                key={category.id} 
                className="group transform transition-all duration-700 hover:scale-105"
                style={{ animationDelay: `${600 + index * 100}ms` }}
              >
                <CategoryCard 
                  id={category.id}
                  title={category.title}
                  description={category.description}
                />
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Add CSS Animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default ProductPage;